// src/pages/admin/PageSystemHealth.jsx
import { useState } from "react";
import * as api from "../../apiService";

const IDLE    = "idle";
const LOADING = "loading";
const OK      = "ok";
const ERROR   = "error";

function statusColor(s) {
  if (s === OK)      return "#15803d";
  if (s === ERROR)   return "#b91c1c";
  if (s === LOADING) return "#c2410c";
  return "#9ca3af";
}

function statusBadge(s) {
  if (s === OK)      return { label: "Connected",   cls: "badge badge-green" };
  if (s === ERROR)   return { label: "Unreachable", cls: "badge badge-amber" };
  if (s === LOADING) return { label: "Checking…",   cls: "badge badge-gray"  };
  return                       { label: "Not tested",  cls: "badge badge-gray"  };
}

function HealthCard({ title, subtitle, status, lastChecked, detail, onTest }) {
  const [expanded, setExpanded] = useState(false);
  const badge = statusBadge(status);

  return (
    <div className="card" style={{ padding: 20, marginBottom: 14 }}>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom: 8 }}>
        <div style={{
          width: 12, height: 12, borderRadius: "50%",
          background: statusColor(status), flexShrink: 0,
          boxShadow: status === OK ? "0 0 0 3px #dcfce7" : "none",
        }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{title}</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>{subtitle}</div>
        </div>
        <span className={badge.cls}>{badge.label}</span>
        <button className="btn-small" onClick={onTest} disabled={status === LOADING}>
          {status === LOADING ? "Testing…" : "Test"}
        </button>
      </div>

      {lastChecked && (
        <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: detail ? 8 : 0 }}>
          Last checked: {lastChecked}
        </div>
      )}

      {detail && (
        <>
          <button className="btn" style={{ fontSize: 11, padding: "2px 10px", marginBottom: 6 }}
            onClick={() => setExpanded(e => !e)}>
            {expanded ? "Hide response ▲" : "Show response ▼"}
          </button>
          {expanded && (
            <pre style={{
              background: "#f8fafc", border: "1px solid #e2e8f0",
              borderRadius: 6, padding: 12, fontSize: 11,
              maxHeight: 300, overflow: "auto", whiteSpace: "pre-wrap",
              wordBreak: "break-all", margin: 0,
            }}>
              {typeof detail === "string" ? detail : JSON.stringify(detail, null, 2)}
            </pre>
          )}
        </>
      )}
    </div>
  );
}

export function PageSystemHealth() {
  const [appStatus,    setAppStatus]    = useState(IDLE);
  const [appDetail,    setAppDetail]    = useState(null);
  const [appChecked,   setAppChecked]   = useState(null);

  const [amiStatus,    setAmiStatus]    = useState(IDLE);
  const [amiDetail,    setAmiDetail]    = useState(null);
  const [amiChecked,   setAmiChecked]   = useState(null);

  const [amtStatus,    setAmtStatus]    = useState(IDLE);
  const [amtDetail,    setAmtDetail]    = useState(null);
  const [amtChecked,   setAmtChecked]   = useState(null);

  function now() {
    return new Date().toLocaleString("en-AE", { dateStyle:"short", timeStyle:"medium" });
  }

  async function testApp() {
    setAppStatus(LOADING);
    try {
      const res = await api.fetchAppHealth();
      setAppStatus(OK);
      setAppDetail(res);
    } catch (e) {
      setAppStatus(ERROR);
      setAppDetail(e.message);
    }
    setAppChecked(now());
  }

  async function testAmi() {
    setAmiStatus(LOADING);
    try {
      const res = await api.fetchTallyPingAmi();
      setAmiStatus(OK);
      setAmiDetail(res);
    } catch (e) {
      setAmiStatus(ERROR);
      setAmiDetail(e.message);
    }
    setAmiChecked(now());
  }

  async function testAmt() {
    setAmtStatus(LOADING);
    try {
      const res = await api.fetchTallyPingAmt();
      setAmtStatus(OK);
      setAmtDetail(res);
    } catch (e) {
      setAmtStatus(ERROR);
      setAmtDetail(e.message);
    }
    setAmtChecked(now());
  }

  async function testAll() {
    await Promise.allSettled([testApp(), testAmi(), testAmt()]);
  }

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">System Health</div>
          <div className="page-subtitle">App backend and TallyPrime gateway connectivity</div>
        </div>
        <div className="page-header-right">
          <button className="btn btn-primary" onClick={testAll}>Test All</button>
        </div>
      </div>

      <HealthCard
        title="App Backend"
        subtitle="Spring Boot API — /api/health"
        status={appStatus}
        lastChecked={appChecked}
        detail={appDetail}
        onTest={testApp}
      />

      <HealthCard
        title="TallyPrime — AMI (Port 9001)"
        subtitle="Al Mehraj Industries Company LLC — /api/admin/tally/ping/ami"
        status={amiStatus}
        lastChecked={amiChecked}
        detail={amiDetail}
        onTest={testAmi}
      />

      <HealthCard
        title="TallyPrime — AMT (Port 9002)"
        subtitle="Al Mehraj General Trading LLC — /api/admin/tally/ping/amt"
        status={amtStatus}
        lastChecked={amtChecked}
        detail={amtDetail}
        onTest={testAmt}
      />
    </>
  );
}
